import React from 'react';
import { motion } from 'framer-motion';

const SplashPerfection = () => {
  // This component is now purely visual - no navigation logic
  // All routing is handled by AppEntry in App.js

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ 
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        paddingTop: 'max(env(safe-area-inset-top), 20px)',
        paddingBottom: 'max(env(safe-area-inset-bottom, 20px), 100px)',
      }}
    >
      {/* Animated cosmic nebulae */}
      <motion.div
        className="absolute"
        style={{
          width: '700px',
          height: '700px',
          background: 'radial-gradient(circle, rgba(0, 255, 255, 0.12) 0%, rgba(138, 43, 226, 0.08) 50%, transparent 80%)',
          filter: 'blur(80px)',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center">
        
        {/* Stars - Connected by smooth curved arc, positioned closer to header */}
        <motion.div
          className="mb-3 md:mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {/* Guiding light spotlight on stars */}
          <motion.div
            style={{
              position: 'absolute',
              width: '200px',
              height: '200px',
              background: 'radial-gradient(circle, rgba(116, 179, 255, 0.4) 0%, transparent 70%)',
              filter: 'blur(40px)',
              pointerEvents: 'none',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 5,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.5, delay: 0.1, ease: 'easeInOut' }}
          />
          
          <svg 
            width="100%" 
            height="50" 
            viewBox="0 0 200 50" 
            style={{ maxWidth: '220px', display: 'block', margin: '0 auto' }}
          >
            <defs>
              <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#92FFF7" stopOpacity="0.7" />
                <stop offset="50%" stopColor="#74B3FF" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#5F42C9" stopOpacity="0.7" />
              </linearGradient>
              <filter id="starGlow">
                <feGaussianBlur stdDeviation="2" result="blur"/>
                <feMerge>
                  <feMergeNode in="blur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {/* Smooth curved arc connecting all stars - perfectly aligned */}
            <motion.path
              d="M 30 40 Q 65 16, 100 10 T 170 40"
              stroke="url(#arcGrad)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              filter="url(#starGlow)"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            />
            
            {/* 5 stars - perfectly aligned on arc curve */}
            {[
              { x: 30, y: 40, size: 7, color: '#92FFF7' },
              { x: 65, y: 16, size: 9, color: '#82D9F9' },
              { x: 100, y: 10, size: 11, color: '#74B3FF' },
              { x: 135, y: 16, size: 9, color: '#6A7BE8' },
              { x: 170, y: 40, size: 7, color: '#5F42C9' },
            ].map((star, i) => (
              <motion.path
                key={i}
                d={`
                  M ${star.x} ${star.y - star.size}
                  L ${star.x + star.size * 0.35} ${star.y - star.size * 0.35}
                  L ${star.x + star.size} ${star.y}
                  L ${star.x + star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x} ${star.y + star.size}
                  L ${star.x - star.size * 0.35} ${star.y + star.size * 0.35}
                  L ${star.x - star.size} ${star.y}
                  L ${star.x - star.size * 0.35} ${star.y - star.size * 0.35}
                  Z
                `}
                fill={star.color}
                filter="url(#starGlow)"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, delay: 0.2 + i * 0.03, ease: [0.34, 1.56, 0.64, 1] }}
              />
            ))}
          </svg>
        </motion.div>

        {/* taraanaya text - Responsive with original logo font */}
        <motion.div
          className="px-4 relative"
          initial={{ opacity: 0, y: 15, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Guiding light spotlight on header */}
          <motion.div
            style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              background: 'radial-gradient(circle, rgba(116, 179, 255, 0.5) 0%, transparent 70%)',
              filter: 'blur(50px)',
              pointerEvents: 'none',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.5, delay: 0.4, ease: 'easeInOut' }}
          />
          
          {/* Glow layer */}
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontFamily: "'Dancing Script', 'Pacifico', cursive",
              fontSize: 'clamp(40px, 8vw, 56px)',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #92FFF7, #74B3FF, #5F42C9)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '2px',
              filter: 'blur(25px)',
              opacity: 0.7,
              whiteSpace: 'nowrap',
              zIndex: 2,
            }}
          >
            taraanaya
          </div>
          
          {/* Main text - Original logo colors */}
          <h1
            style={{
              fontFamily: "'Dancing Script', 'Pacifico', cursive",
              fontSize: 'clamp(40px, 8vw, 56px)',
              fontWeight: 400,
              background: 'linear-gradient(90deg, #92FFF7 0%, #74B3FF 50%, #5F42C9 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '2px',
              textAlign: 'center',
              filter: 'drop-shadow(0 0 35px rgba(146, 255, 247, 0.7)) drop-shadow(0 0 60px rgba(116, 179, 255, 0.5))',
              position: 'relative',
              whiteSpace: 'nowrap',
              zIndex: 3,
            }}
          >
            taraanaya
          </h1>
        </motion.div>

        {/* Subtitle - Responsive */}
        <motion.div className="relative px-4">
          {/* Guiding light spotlight on subtitle */}
          <motion.div
            style={{
              position: 'absolute',
              width: '250px',
              height: '250px',
              background: 'radial-gradient(circle, rgba(116, 179, 255, 0.4) 0%, transparent 70%)',
              filter: 'blur(45px)',
              pointerEvents: 'none',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 0.4, delay: 0.7, ease: 'easeInOut' }}
          />
          
          <motion.p
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 'clamp(13px, 3vw, 16px)',
              fontWeight: 600,
              color: '#E2E8F0',
              letterSpacing: 'clamp(3px, 1vw, 5px)',
              textAlign: 'center',
              marginTop: 'clamp(16px, 4vw, 24px)',
              textShadow: '0 2px 20px rgba(226, 232, 240, 0.6)',
              position: 'relative',
              zIndex: 2,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            DECISION INTELLIGENCE
          </motion.p>
        </motion.div>

      </div>

      {/* Tagline - Positioned above bottom but below subtitle */}
      <motion.div
        className="absolute left-0 right-0 z-50"
        style={{
          bottom: 'clamp(180px, 28vh, 260px)',
          paddingBottom: 'max(env(safe-area-inset-bottom, 20px), 20px)',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {/* Guiding light spotlight on tagline - stays longest */}
        <motion.div
          style={{
            position: 'absolute',
            width: '450px',
            height: '220px',
            background: 'radial-gradient(ellipse, rgba(116, 179, 255, 0.4) 0%, transparent 70%)',
            filter: 'blur(50px)',
            pointerEvents: 'none',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.8, 1, 1, 1] }}
          transition={{ duration: 2.0, delay: 1.0, ease: 'easeInOut' }}
        />
        
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 'clamp(10px, 2.8vw, 12px)',
            fontWeight: 600,
            color: '#E2E8F0',
            letterSpacing: 'clamp(2px, 0.7vw, 4px)',
            textAlign: 'center',
            textShadow: '0 2px 20px rgba(226, 232, 240, 0.8), 0 0 40px rgba(0, 255, 255, 0.3)',
            padding: '0 clamp(20px, 8vw, 40px)',
            lineHeight: '1.6',
            position: 'relative',
            zIndex: 2,
          }}
        >
          PLATFORM POWERED BY ASTROLOGY
        </p>
      </motion.div>

      {/* Floating particles */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full"
          style={{
            width: Math.random() * 3 + 1,
            height: Math.random() * 3 + 1,
            background: i % 3 === 0 ? '#00FFFF' : i % 3 === 1 ? '#00CED1' : '#8A2BE2',
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -25, 0],
            opacity: [0.2, 0.7, 0.2],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export default SplashPerfection;
